import React, { useEffect, useState } from 'react';
import { BillingConfig, adminGetBillingConfig, adminUpdateBillingConfig } from '../api.billing';
import { PlatformFeeSettings, adminGetPlatformFee, adminUpdatePlatformFee } from '../api.platformFee';

const getToken = () => localStorage.getItem('token') || '';

const formatCzk = (n: number) => `${n.toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Kč`;

/** max(percentage%, flatAmountCzk), capped so it never reaches the total — mirrors
 * PlatformFeeServiceImpl#calculateFeeHalers exactly (computed in halers, same rounding). */
const previewFee = (totalCzk: number, percentage: number, flatAmountCzk: number): number => {
  const totalHalers = Math.round(totalCzk * 100);
  const feeFromPercent = Math.round((totalHalers * percentage) / 100);
  const feeFromFlat = Math.round(flatAmountCzk * 100);
  let fee = Math.max(feeFromPercent, feeFromFlat);
  if (fee >= totalHalers) fee = totalHalers - 1;
  return Math.max(fee, 0) / 100;
};

const AdminBillingSettings: React.FC = () => {
  const [form, setForm] = useState<BillingConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [feeForm, setFeeForm] = useState<PlatformFeeSettings | null>(null);
  const [feeLoading, setFeeLoading] = useState(true);
  const [feeSaving, setFeeSaving] = useState(false);
  const [feeError, setFeeError] = useState<string | null>(null);
  const [feeSuccess, setFeeSuccess] = useState(false);

  useEffect(() => {
    adminGetBillingConfig(getToken())
      .then(c => setForm(c))
      .catch(err => setError(err?.response?.data?.message || err?.message || 'Failed to load'))
      .finally(() => setLoading(false));

    adminGetPlatformFee(getToken())
      .then(f => setFeeForm(f))
      .catch(err => setFeeError(err?.response?.data?.message || err?.message || 'Failed to load'))
      .finally(() => setFeeLoading(false));
  }, []);

  const handleChange = (field: keyof BillingConfig, value: number | boolean | string) => {
    if (!form) return;
    setForm({ ...form, [field]: value });
    setSuccess(false);
  };

  const handleSave = async () => {
    if (!form) return;
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const updated = await adminUpdateBillingConfig(form, getToken());
      setForm(updated);
      setSuccess(true);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleFeeChange = (field: keyof PlatformFeeSettings, value: number) => {
    if (!feeForm) return;
    setFeeForm({ ...feeForm, [field]: value });
    setFeeSuccess(false);
  };

  const handleFeeSave = async () => {
    if (!feeForm) return;
    setFeeSaving(true);
    setFeeError(null);
    setFeeSuccess(false);
    try {
      const updated = await adminUpdatePlatformFee(feeForm, getToken());
      setFeeForm(updated);
      setFeeSuccess(true);
    } catch (err: any) {
      setFeeError(err?.response?.data?.message || err?.message || 'Failed to save');
    } finally {
      setFeeSaving(false);
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#718096' }}>Loading...</div>;
  if (!form) return <div style={{ padding: 16, color: '#c53030' }}>{error || 'No config found'}</div>;

  return (
    <div className="page-container">
      <h1 style={{ fontSize: '1.75rem', fontWeight: 700, marginBottom: 2, color: '#1a202c' }}>Billing Settings</h1>
      <p style={{ color: '#718096', marginBottom: 24, fontSize: '0.9rem' }}>Global pricing and suspension enforcement rules</p>

      {error && (
        <div style={{ background: '#fff5f5', border: '1px solid #fed7d7', borderRadius: 8, padding: '10px 14px', color: '#c53030', marginBottom: 16 }}>
          {error}
        </div>
      )}
      {success && (
        <div style={{ background: '#f0fff4', border: '1px solid #9ae6b4', borderRadius: 8, padding: '10px 14px', color: '#276749', marginBottom: 16 }}>
          Settings saved successfully.
        </div>
      )}

      <div style={{ display: 'grid', gap: 20 }}>

        <section style={{ background: '#fff', borderRadius: 10, padding: 20, boxShadow: '0 1px 4px rgba(15,23,42,0.06)', border: '1px solid #e2e8f0' }}>
          <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1a202c', marginBottom: 16 }}>Pricing</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#4a5568' }}>Monthly price per apartment</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input
                  type="number"
                  min={0}
                  value={form.pricePerApartmentMonthly ?? ''}
                  onChange={e => handleChange('pricePerApartmentMonthly', Number(e.target.value))}
                  style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 10px', fontSize: '0.95rem', width: '100%' }}
                />
                <span style={{ color: '#718096', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>{form.currency || 'CZK'}</span>
              </div>
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#4a5568' }}>Annual price per apartment</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input
                  type="number"
                  min={0}
                  value={form.pricePerApartmentAnnual ?? ''}
                  onChange={e => handleChange('pricePerApartmentAnnual', Number(e.target.value))}
                  style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 10px', fontSize: '0.95rem', width: '100%' }}
                />
                <span style={{ color: '#718096', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>{form.currency || 'CZK'}</span>
              </div>
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#4a5568' }}>Currency</span>
              <input
                type="text"
                value={form.currency ?? ''}
                onChange={e => handleChange('currency', e.target.value)}
                style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 10px', fontSize: '0.95rem' }}
              />
            </label>
          </div>
        </section>

        <section style={{ background: '#fff', borderRadius: 10, padding: 20, boxShadow: '0 1px 4px rgba(15,23,42,0.06)', border: '1px solid #e2e8f0' }}>
          <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1a202c', marginBottom: 4 }}>Suspension Enforcement</div>
          <p style={{ color: '#718096', fontSize: '0.85rem', marginBottom: 16 }}>
            When a manager is SUSPENDED, which actions should be blocked? (Suspended managers can always view data.)
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {(
              [
                { field: 'suspendBlockReservations' as keyof BillingConfig, label: 'Block creating / editing reservations' },
                { field: 'suspendBlockApartments' as keyof BillingConfig, label: 'Block creating / editing apartments' },
                { field: 'suspendBlockCalendarSync' as keyof BillingConfig, label: 'Block calendar sync (scheduled)' },
                { field: 'suspendBlockExport' as keyof BillingConfig, label: 'Block data export (CSV / PDF)' },
                { field: 'suspendBlockUbyport' as keyof BillingConfig, label: 'Block submitting to Ubyport' },
              ]
            ).map(({ field, label }) => (
              <label key={field} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={!!form[field]}
                  onChange={e => handleChange(field, e.target.checked)}
                  style={{ width: 16, height: 16, cursor: 'pointer' }}
                />
                <span style={{ fontSize: '0.9rem', color: '#2d3748' }}>{label}</span>
              </label>
            ))}
          </div>
        </section>

      </div>

      <div style={{ marginTop: 20 }}>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8,
            padding: '10px 24px', fontWeight: 600, fontSize: '0.95rem', cursor: saving ? 'not-allowed' : 'pointer',
            opacity: saving ? 0.7 : 1,
          }}
        >
          {saving ? 'Saving…' : 'Save Settings'}
        </button>
      </div>

      {/* ── City Tax Application Fee ────────────────────────────────────────
          Independent of the subscription pricing above — a per-transaction fee deducted from
          managers' Stripe Connect payouts on city-tax collection, not the SaaS subscription. */}
      <div style={{ marginTop: 20 }}>
        <section style={{ background: '#fff', borderRadius: 10, padding: 20, boxShadow: '0 1px 4px rgba(15,23,42,0.06)', border: '1px solid #e2e8f0' }}>
          <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1a202c', marginBottom: 4 }}>City Tax Application Fee</div>
          <p style={{ color: '#718096', fontSize: '0.85rem', marginBottom: 16 }}>
            Charged on each city-tax payment collected via Stripe Connect — max(percentage, flat amount),
            deducted from the manager's payout. The guest's tax total is never affected.
          </p>

          {feeError && (
            <div style={{ background: '#fff5f5', border: '1px solid #fed7d7', borderRadius: 8, padding: '10px 14px', color: '#c53030', marginBottom: 16 }}>
              {feeError}
            </div>
          )}
          {feeSuccess && (
            <div style={{ background: '#f0fff4', border: '1px solid #9ae6b4', borderRadius: 8, padding: '10px 14px', color: '#276749', marginBottom: 16 }}>
              Fee settings saved successfully.
            </div>
          )}

          {feeLoading && <div style={{ color: '#718096', fontSize: '0.9rem' }}>Loading...</div>}

          {!feeLoading && feeForm && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 20 }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#4a5568' }}>Percentage</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      step={0.01}
                      value={feeForm.percentage ?? ''}
                      onChange={e => handleFeeChange('percentage', Number(e.target.value))}
                      style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 10px', fontSize: '0.95rem', width: '100%' }}
                    />
                    <span style={{ color: '#718096', fontSize: '0.85rem' }}>%</span>
                  </div>
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#4a5568' }}>Flat minimum</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input
                      type="number"
                      min={0}
                      step={0.01}
                      value={feeForm.flatAmountCzk ?? ''}
                      onChange={e => handleFeeChange('flatAmountCzk', Number(e.target.value))}
                      style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 10px', fontSize: '0.95rem', width: '100%' }}
                    />
                    <span style={{ color: '#718096', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>Kč</span>
                  </div>
                </label>
              </div>

              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16, marginBottom: 20 }}>
                <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#718096', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 10 }}>
                  Example
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
                  {[100, 1200].map(example => (
                    <div key={example}>
                      <div style={{ fontSize: '0.85rem', color: '#718096' }}>City tax total: {formatCzk(example)}</div>
                      <div style={{ fontSize: '1.15rem', fontWeight: 700, color: '#1a202c' }}>
                        Fee: {formatCzk(previewFee(example, feeForm.percentage, feeForm.flatAmountCzk))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <button
                onClick={handleFeeSave}
                disabled={feeSaving}
                style={{
                  background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8,
                  padding: '10px 24px', fontWeight: 600, fontSize: '0.95rem', cursor: feeSaving ? 'not-allowed' : 'pointer',
                  opacity: feeSaving ? 0.7 : 1,
                }}
              >
                {feeSaving ? 'Saving…' : 'Save Fee Settings'}
              </button>
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default AdminBillingSettings;
